<template>
  <div v-if="referralNumber">
    <MatchMedia>
      <template v-for="(section, key) in sectionsWithDueTotal">
        <div :key="key" class="mt-3">
          <b-skeleton-wrapper :loading="loading">
            <template #loading>
              <b-card>
                <b-row>
                  <b-col>
                    <b-skeleton width="85%" />
                    <b-skeleton width="55%" />
                  </b-col>
                  <b-col>
                    <b-skeleton width="70%" />
                    <b-skeleton width="55%" />
                  </b-col>
                  <b-col>
                    <b-skeleton width="85%" />
                    <b-skeleton width="55%" />
                  </b-col>
                  <b-col>
                    <b-skeleton width="70%" />
                    <b-skeleton width="55%" />
                  </b-col>
                  <b-col>
                    <b-skeleton type="button" width="100%" />
                  </b-col>
                </b-row>
              </b-card>
            </template>

            <template v-for="(item, i) in section.items">
              <b-card
                :key="i"
                class="bg-light-gray border-0"
                :class="{ 'mt-2': i > 0 }"
              >
                <b-row>
                  <template
                    v-if="
                      key === INTERVENTION_TYPES.REQUEST_FOR_COMPENSATORY_HOURS
                    "
                  >
                    <b-col cols="12" md>
                      <dl class="m-0">
                        <dd class="m-0">
                          <Balloon
                            :bg="`bg-${getStatusColor(item.statusId)}`"
                            class="border-none text-center font-15"
                            dot
                            :text="item.status"
                          />
                        </dd>
                        <dd class="m-0">{{ item.requestNumber }}</dd>
                      </dl>
                    </b-col>
                  </template>

                  <b-col
                    :class="{
                      'mt-2 mt-md-0':
                        key ===
                        INTERVENTION_TYPES.REQUEST_FOR_COMPENSATORY_HOURS,
                    }"
                    cols="12"
                    md
                  >
                    <dl class="m-0 text-md-center">
                      <dd class="m-0 text-muted small">
                        Año Académico:
                      </dd>
                      <dd class="m-0">{{ item.academicYearDesc }}</dd>
                    </dl>
                  </b-col>
                  <b-col class="mt-2 mt-md-0" cols="12" md>
                    <dl class="m-0 text-md-center">
                      <dd class="m-0 text-muted small">
                        Procedencia:
                      </dd>
                      <dd class="m-0">{{ item.detail.sourceApp }}</dd>
                    </dl>
                  </b-col>
                  <b-col class="mt-2 mt-md-0" cols="12" md>
                    <dl class="m-0 text-md-center">
                      <dd class="m-0 text-muted small">
                        Intervenciones Adeudadas:
                      </dd>
                      <dd class="m-0">{{ item.interventionsDue }}</dd>
                    </dl>
                  </b-col>
                  <b-col class="mt-2 mt-md-0" cols="12" md>
                    <dl class="m-0 text-md-center">
                      <dd class="m-0 text-muted small">
                        Intervenciones Ofrecidas:
                      </dd>
                      <dd class="m-0">{{ item.interventionsOffered }}</dd>
                    </dl>
                  </b-col>
                  <b-col class="mt-2 mt-md-0" cols="12" md>
                    <dl class="m-0 text-md-center">
                      <dd class="m-0 text-muted small">
                        Balance:
                      </dd>
                      <dd class="m-0">
                        {{
                          isRejectedOrCompletedStatus(item)
                            ? 0
                            : item.interventionsDueTotal
                        }}
                      </dd>
                    </dl>
                  </b-col>
                  <b-col class="mt-2 mt-md-0 text-md-right" cols="12" md>
                    <template
                      v-if="
                        key ===
                          INTERVENTION_TYPES.REQUEST_FOR_COMPENSATORY_HOURS
                      "
                    >
                      <DropDown :data-item="{ item, i }" :items="actionItems" />
                    </template>
                  </b-col>
                </b-row>
                <error
                  v-if="item.isSigned && item.selectedToCompensate"
                  is-alert-mode
                  message="La planilla fue marcada para compensar los servicios durante el año, mediante la revisión de PEI."
                />
              </b-card>
            </template>

            <CustomResultNotFound
              v-if="section.items && section.items.length === 0"
              :text="`No posee registros de ${sections[key].title}`"
            />
          </b-skeleton-wrapper>
        </div>
      </template>
    </MatchMedia>
  </div>
</template>

<script>
import { MatchMedia } from 'vue-component-media-queries';
import CustomResultNotFound from 'custom-result-not-found';
import statusColorMixin from '@/views/provider/referral-management/common/statusColor';
import compensatoryServiceStatus from '@/utils/constants/compensatoryServiceStatus.js';
import { api } from '@/core/api';
import Error from 'custom-error';
import compensatoryRequestStatus from '@/utils/constants/compensatoryRequestStatus.js';
import alert from '@/mixins/alert.mixin';
import { getVisitStudentInfo as _getVisitStudentInfo } from '@/modules/billingHistory/api';
import { getSysparameterByNames as _getSysparameterByNames } from '@/modules/shared/sysparameter.api';
import sysParameterNames from '@/utils/constants/sysParameterNames';
import { getCurrentAcademicYear } from '@/modules/shared/academicyear.api';
import moment from 'moment';
import { compensatoryRequestType } from 'utils/consts/compensatoryRequestType';

const INTERVENTION_TYPES = {
  REQUEST_FOR_COMPENSATORY_HOURS: 'REQUEST_FOR_COMPENSATORY_HOURS',
};

export default {
  name: 'InterventionsDue',
  components: { MatchMedia, CustomResultNotFound, Error },
  mixins: [alert, statusColorMixin],
  props: {
    referralNumber: { type: String, default: null, required: true },
    studentName: { type: String, default: null, required: false },
    disciplineName: { type: String, default: null, required: false },
    serviceSpecialistName: { type: String, default: null, required: false },
    studentSieId: { type: Number, default: null, required: true },
    disciplineTypeId: { type: Number, default: null, required: true },
    academicYearId: { type: Number, default: null, required: true },
  },
  data: () => ({
    loading: false,
    sections: {
      [INTERVENTION_TYPES.REQUEST_FOR_COMPENSATORY_HOURS]: {
        title: 'Solicitud de horas compensatorias',
        items: [],
      },
    },
    extendedYearParamaters: [],
    INTERVENTION_TYPES,
    currentAcademicYearId: null,
  }),
  computed: {
    actionItems() {
      return [
        {
          name: 'Ver Solicitud',
          label: 'Ver Solicitud',
          icon: 'fas fa-file-invoice color-primary-1',
          click: ({ item }) => {
            this.onRequestNavigation(item);
          },
          visible: this.viewMenuEditAction,
        },
        {
          name: 'Análisis de Cierre',
          label: 'Análisis de Cierre',
          icon: 'fas fa-clipboard-list-check color-primary-1',
          click: ({ item }) => {
            if (item.interventionsDue == item.interventionsDueTotal) {
              this.ShowToast(
                'Alerta',
                'No es posible realizar el análisis de cierre ya que no cuenta con visitas registradas',
                'error'
              );
              return;
            }
            this.$router.push({
              name: 'ClosingAnalysis',
              params: { id: item.detail.requestId },
            });
          },
          visible: ({ item }) => {
            return (
              this.userHasPermissions(
                'provider.compensatoryservice.canviewclosinganalysis'
              ) &&
              item.detail.isReposition &&
              (item.statusId == compensatoryServiceStatus.Aprobado ||
                item.statusId == compensatoryServiceStatus.AprobadoModificado ||
                item.statusId ==
                  compensatoryServiceStatus.CompletadoPendienteAprobacion ||
                item.statusId == compensatoryServiceStatus.ProcesoCompletado ||
                item.statusId == compensatoryServiceStatus.DevueltoAProveedor)
            );
          },
        },
        {
          name: 'Registro de Visitas',
          label: 'Registro de Visitas',
          icon: 'fas fa-calendar-edit color-primary-1',
          click: ({ item }) => this.onVisitRegistryNavigateHandler(item),
          visible: ({ item }) => {
            return (
              [
                compensatoryServiceStatus.ProcesoCompletado,
                compensatoryServiceStatus.Aprobado,
                compensatoryServiceStatus.AprobadoModificado,
              ].includes(item.statusId) &&
              item.detail.isReposition &&
              item.academicYearId === this.currentAcademicYearId &&
              this.isExtendedPeriod
            );
          },
        },
      ];
    },
    sectionsWithDueTotal() {
      return Object.entries(this.sections).reduce(
        (sum, [key, x]) => ({
          ...sum,
          [key]: {
            ...x,
            items: x.items.map((y) => ({
              ...y,
            })),
          },
        }),
        {}
      );
    },
    flattenedSectionsWithDueTotal() {
      return Object.values(this.sectionsWithDueTotal)
        .map((x) => x.items)
        .flat();
    },
    interventionsSum() {
      return this.flattenedSectionsWithDueTotal.reduce(
        (sum, { interventionsDue }) => +interventionsDue + sum,
        0
      );
    },
    interventionsOfferedSum() {
      return this.flattenedSectionsWithDueTotal.reduce(
        (sum, { interventionsOffered }) => +interventionsOffered + sum,
        0
      );
    },
    interventionsTotalSum() {
      return this.flattenedSectionsWithDueTotal.reduce(
        (sum, { interventionsDueTotal }) => +interventionsDueTotal + sum,
        0
      );
    },
    getStatusColor() {
      return (statusId) => {
        let color = 'primary-13';
        switch (statusId) {
          case compensatoryRequestStatus.Approved:
          case compensatoryRequestStatus.RequestCompleted:
          case compensatoryRequestStatus.ProcessCompleted:
            color = 'green';
            break;
          case compensatoryRequestStatus.RequestRejected:
            color = 'brown';
            break;
          case compensatoryRequestStatus.ReturnedToSupplier:
            color = 'primary-16';
            break;

          default:
            break;
        }
        return color;
      };
    },
    isRejectedOrCompletedStatus() {
      return (item) => {
        const rejectedBalanceStatuses = [
          compensatoryRequestStatus.RequestRejected,
          compensatoryRequestStatus.ExcludedByStrategy,
          compensatoryRequestStatus.ExcludedGraduated,
          compensatoryRequestStatus.ExcludedServiceHigh,
          compensatoryRequestStatus.RequestCompleted,
        ];

        return rejectedBalanceStatuses.includes(item.statusId);
      };
    },
    isExtendedPeriod() {
      const today = new Date();
      return this.extendedYearParamaters.every(
        (x) =>
          moment(x.rangeMinimumValue).toDate() <= today &&
          today <= moment(x.rangeMaximumValue).toDate()
      );
    },
  },
  async created() {
    await this.getInterventionsDue();
    this.currentAcademicYearId = (await getCurrentAcademicYear()).data.id;
    this.extendedYearParamaters = (
      await _getSysparameterByNames({
        parameterNames: [
          sysParameterNames.RegressionExtendedAcademicYearDateRange,
          sysParameterNames.ReplenishmentExtendedAcademicYearDateRange,
        ],
        academicYearId: this.currentAcademicYearId,
      })
    ).data;
  },
  methods: {
    async onVisitRegistryNavigateHandler(item) {
      if (item) {
        const monthNumber = 6;
        const academicYearId = item.academicYearId;
        const academicYearDescription = item.academicYearDesc;
        const admissionId = item.detail.admissionId;
        const studentServiceId = item.detail.studentServiceId;
        const monthDate = new Date();
        const academicYearDesFrom = parseInt(
          academicYearDescription.split('-')[1]
        );

        //get student visit info
        const { data: studentInfo } = await _getVisitStudentInfo(
          studentServiceId,
          academicYearId,
          monthDate,
          admissionId
        );

        const requestType = item.detail.isReposition
          ? { id: compensatoryRequestType.Reposicion, value: 'Reposición' }
          : { id: compensatoryRequestType.Regresion, value: 'Regresión' };

        this.$router.push({
          name: 'VisitRegistryValidate',
          params: {
            ...studentInfo,
            admissionId,
            academicYearId: academicYearId,
            visitStudentServiceId: studentServiceId,
            serviceDate: monthDate,
            initialPeriodInfo: {
              monthNumber,
              year: academicYearDescription.split('-')[0],
            },
            currentPeriod: {
              monthNumber,
              year: academicYearDesFrom,
            },
            isCompensatory: true,
            compensatoryInfo: {
              requestType: requestType.value,
              requestTypeId: requestType.id,
              requestNumber: item.requestNumber,
              interventionAmount: item.interventionsDueTotal,
              serviceSpecialistId: item.specialistId,
            },
            disabledPeriods: [],
            filter: item.filter,
          },
        });
      }
    },
    calcInterventionsDueTotal(a, b) {
      return Math.abs(+a - +b);
    },
    async onRequestNavigation(item) {
      const request = item.detail;
      this.$router.push({
        name: 'CompensatoryRequestFromReferralManagement',
        query: {
          isReposition: request.isReposition,
        },
        params: {
          studentSie: this.studentSieId,
          academicYearId: item.academicYearId,
          ...request,
          ...this.$route.params,
          readOnly: item.statusId !== compensatoryServiceStatus.Nuevo,
          from: '/referral/search',
        },
      });
    },
    async getInterventionsDue() {
      this.loading = true;
      try {
        const excludedStatusIds = [
          compensatoryServiceStatus.SolicitudRechazada,
        ];
        const { data } = await api.get(
          `/api/compensatoryrequest/interventionsDue/${this.studentSieId}/${this.disciplineTypeId}`,
          {
            params: {
              excludedStatusIds: excludedStatusIds,
            },
          }
        );
        if (data) {
          const { compensatoryHourRequests } = data;
          this.sections[
            INTERVENTION_TYPES.REQUEST_FOR_COMPENSATORY_HOURS
          ].items =
            compensatoryHourRequests != null
              ? compensatoryHourRequests.map((data) => {
                  return {
                    ...data,
                    detail: {
                      ...data.detail,
                      studentName: this.studentName,
                      disciplineName: this.disciplineName,
                      serviceSpecialistName: this.serviceSpecialistName,
                    },
                  };
                })
              : [];
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
